/**
 * ErrorFallback (task_015)
 *
 * Componente fallback do ErrorBoundary global (§17 da spec). Renderizado
 * quando uma exceção não tratada ocorre em qualquer componente abaixo do
 * boundary.
 *
 * Compatível com a assinatura `FallbackComponent` do `react-error-boundary`:
 *   - `error: Error` — a exceção lançada
 *   - `resetErrorBoundary?: () => void` — opcional; quando fornecido, é
 *     chamado pelo botão "Recarregar". Sem ele, fazemos `location.reload()`.
 *
 * Comportamento:
 *   • Em produção, mostra apenas mensagem genérica i18n + botão.
 *   • Em desenvolvimento (`import.meta.env.DEV`), expande os "Detalhes
 *     técnicos" com `error.message` e `error.stack` para acelerar debug.
 */
import * as React from 'react'
import { AlertTriangle, RotateCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'

/**
 * Props compatíveis com `react-error-boundary`:
 * https://github.com/bvaughn/react-error-boundary#fallbackcomponent
 *
 * Mantemos `resetErrorBoundary` opcional para que `ErrorFallback` também
 * funcione fora de um boundary (ex: em rotas de erro do React Router).
 */
export interface ErrorFallbackProps {
  error: Error
  resetErrorBoundary?: () => void
}

export function ErrorFallback({
  error,
  resetErrorBoundary,
}: ErrorFallbackProps): React.ReactElement {
  const { t } = useTranslation('common')

  function handleReload() {
    if (resetErrorBoundary) {
      // Quando dentro de um ErrorBoundary, preferimos resetar o boundary —
      // isso preserva o estado da app (auth, cache de queries) e apenas
      // re-renderiza a árvore que falhou.
      resetErrorBoundary()
      return
    }
    // Fallback hard: reload completo da página.
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }

  return (
    <div
      role="alert"
      data-testid="error-fallback"
      className="flex min-h-[60vh] w-full flex-col items-center justify-center gap-4 px-6 py-12 text-center"
    >
      <div
        className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive"
        aria-hidden="true"
      >
        <AlertTriangle className="h-8 w-8" />
      </div>

      <div className="space-y-2">
        <h1 className="text-xl font-semibold text-foreground">
          {t('errorBoundary.title')}
        </h1>
        <p className="max-w-md text-sm text-muted-foreground">
          {t('errorBoundary.description')}
        </p>
      </div>

      <Button
        type="button"
        onClick={handleReload}
        data-testid="error-fallback-reload"
        className="mt-2"
      >
        <RotateCw className="h-4 w-4" aria-hidden="true" />
        {t('errorBoundary.reload')}
      </Button>

      {import.meta.env.DEV && error ? (
        <details
          className="mt-4 w-full max-w-2xl rounded-md border border-destructive/30 bg-destructive/5 p-3 text-left"
          data-testid="error-fallback-details"
        >
          <summary className="cursor-pointer text-xs font-medium text-destructive">
            {t('errorBoundary.details')}
          </summary>
          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words text-xs text-destructive/80">
            {error.message}
            {error.stack ? `\n\n${error.stack}` : null}
          </pre>
        </details>
      ) : null}
    </div>
  )
}
